import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import NumberFlow from '@number-flow/react'
import { PiMoney, PiReceipt, PiPercent, PiInfo, PiArrowSquareOut, PiTrendUp } from 'react-icons/pi'

import { currencyFormatOptions, formatCurrency } from '@/utils/currency'

import Header from '@/components/dashboard/Header/Header'
import Navigation from '@/components/dashboard/Navigation/Navigation'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import MotionText from '@/components/common/Motion/MotionText'
import WriteVisual from '@assets/visuals/write.webp'

import { useDashboardStore } from '@/store/useDashboardStore'
import { useUserStore } from '@/store/useUserStore'
import { useTaxProfile } from '@/hooks/useTaxProfile'

import dashboardAPI from '@/services/api/dashboard'
import { calculateTaxes, centsToEuros, getGpmEffectiveRateInfo } from '@/services/taxes/lithuania'
import { VAT_LINKS } from './DashboardVatMemo'
import { getParentCategories as getIncomeParentCategories } from '@/types/income'
import { getParentCategories as getExpenseParentCategories } from '@/types/expense'
import { usePageSetup } from '@/hooks/usePageSetup'

interface TaxRowProps {
  label: string
  value: number
  isPercentage?: boolean
  isCurrency?: boolean
  isHighlight?: boolean
  isSmall?: boolean
  delay?: number
  info?: string
}

const TaxRow = ({ label, value, isPercentage, isCurrency = true, isHighlight, isSmall, delay = 0, info }: TaxRowProps) => {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <MotionDiv delay={delay} className={`flex justify-between items-center ${isSmall ? 'py-1' : 'py-3'} ${isHighlight ? 'mt-1 pt-4 border-t border-dashed border-zinc-300' : ''}`}>
      <div className="flex items-center gap-1">
        <span className={`${isSmall ? 'text-xs' : 'text-sm'} ${isHighlight ? 'font-semibold text-zinc-900' : isSmall ? 'text-zinc-500' : 'text-zinc-700'}`}>
          {label}
        </span>
        {info && (
          <button
            type="button"
            aria-label={`Informacija: ${label}`}
            className="relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded"
            onClick={() => setShowTooltip(!showTooltip)}
            onBlur={() => setShowTooltip(false)}
          >
            <PiInfo className="w-4 h-4 text-zinc-400" />
            <span
              role="tooltip"
              className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-zinc-800 rounded-md w-max max-w-[15rem] whitespace-pre-line text-center leading-snug transition-all duration-200 z-10 ${showTooltip ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            >
              {info}
              <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-zinc-800" />
            </span>
          </button>
        )}
      </div>
      <span className={`${isSmall ? 'text-xs' : 'text-sm'} tabular-nums ${isHighlight ? 'font-semibold text-zinc-900' : isSmall ? 'text-zinc-500' : 'text-zinc-900'}`}>
        {isPercentage
          ? `${(value * 100).toFixed(2)}%`
          : isCurrency
            ? formatCurrency.format(value)
            : value.toFixed(2)
        }
      </span>
    </MotionDiv>
  )
}

const SavingsCard = ({ savings }: { savings: number }) => {
  const { t } = useTranslation()
  return (
  <MotionDiv delay={0.1} className="mb-4">
    <div className="relative bg-emerald-50 border border-emerald-200 rounded-xl p-4 overflow-hidden">
      <img
        src={WriteVisual}
        alt=""
        className="h-[8rem] absolute -top-2 -right-4 opacity-80"
      />
      <p className="text-sm text-emerald-800">
        {t('taxes.willGetBack')}:
      </p>
      <p className="text-2xl font-bold text-emerald-700">
        {formatCurrency.format(savings)}*
      </p>
      <p className="text-xs text-emerald-600 mt-1 max-w-[10rem]">
        {t('taxes.comparedTo30')}
      </p>
    </div>
  </MotionDiv>
  )
}

const InsuranceStatusPrompt = ({ onSetup }: { onSetup: () => void }) => {
  const { t } = useTranslation()
  return (
    <MotionDiv delay={0.05} className="mb-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-600 mt-0.5"><PiInfo className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-zinc-900">{t('taxes.statusPrompt.title')}</p>
            <p className="text-xs text-zinc-600 mt-1">{t('taxes.statusPrompt.description')}</p>
            <button
              type="button"
              onClick={onSetup}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              {t('taxes.statusPrompt.cta')}
            </button>
          </div>
        </div>
      </div>
    </MotionDiv>
  )
}

const TaxCard = ({ title, icon, children, delay = 0, link }: { title: string; icon: React.ReactNode; children: React.ReactNode; delay?: number; link?: string }) => (
  <MotionDiv delay={delay} className="bg-zinc-50 rounded-xl p-4 mb-3">
    <div className="flex items-center mb-3">
      <div className="text-zinc-700 mr-2">{icon}</div>
      <h3 className="text-base font-semibold text-zinc-900">{title}</h3>
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 ml-1">
          <PiArrowSquareOut className="w-4 h-4" />
        </a>
      )}
    </div>
    {children}
  </MotionDiv>
)

export const DashboardTaxesPage = () => {
  const { t } = useTranslation();
  usePageSetup(t('taxes.pageTitle'))
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)
  const { insuranceStatus, firstYearStartedAt } = useTaxProfile()
  const financialYear = useDashboardStore((state) => state.financialYear)
  const dashboard = useDashboardStore((state) => state.dashboard)
  const setDashboard = useDashboardStore((state) => state.setDashboard)

  useEffect(() => {
    dashboardAPI.show({ year: financialYear })
      .then((dashboard) => setDashboard(dashboard))
  }, [financialYear, setDashboard])

  const incomeCategories = dashboard?.categories?.incomes || {}
  const expenseCategories = dashboard?.categories?.expenses || {}

  const incomeItems = useMemo(() =>
    getIncomeParentCategories(t)
      .map(category => ({
        key: category.value,
        description: category.description,
        amount: centsToEuros(incomeCategories[category.value as keyof typeof incomeCategories] || 0)
      }))
      .filter(item => item.amount > 0),
    [incomeCategories, t]
  )

  const expenseItems = useMemo(() =>
    getExpenseParentCategories(t)
      .filter(category => category.value !== 'taxes_and_fees')
      .map(category => ({
        key: category.value,
        label: category.label,
        amount: centsToEuros(expenseCategories[category.value as keyof typeof expenseCategories] || 0)
      }))
      .filter(item => item.amount > 0),
    [expenseCategories, t]
  )

  const totalIncome = useMemo(() =>
    incomeItems.reduce((sum, item) => sum + item.amount, 0),
    [incomeItems]
  )

  const totalExpenses = useMemo(() =>
    expenseItems.reduce((sum, item) => sum + item.amount, 0),
    [expenseItems]
  )

  const taxes = useMemo(() =>
    calculateTaxes(
      totalIncome, totalExpenses, financialYear,
      { method: 'actual', monthsActive: dashboard?.months_active, insuranceStatus, firstYearStartedAt }
    ),
    [totalIncome, totalExpenses, financialYear, dashboard?.months_active, insuranceStatus, firstYearStartedAt]
  )

  /** This is used to provide estimate */
  const taxesWithFixed = useMemo(() =>
    calculateTaxes(
      totalIncome, totalIncome * 0.30, financialYear,
      { method: 'fixed_30', monthsActive: dashboard?.months_active, insuranceStatus, firstYearStartedAt }
    ),
    [totalIncome, financialYear, dashboard?.months_active, insuranceStatus, firstYearStartedAt]
  )

  const savings = taxesWithFixed.totalTax - taxes.totalTax

  return (
    <div className="h-full bg-white flex flex-col">
      <Header />
      <div className="flex-1 p-5 pt-2 pb-[10rem] overflow-y-auto">
        {!user?.kyc?.insurance_status && (
          <InsuranceStatusPrompt
            onSetup={() => navigate('/kyc/insurance-status', { state: { returnTo: '/taxes' } })}
          />
        )}

        <MotionDiv className="mb-6">
          <p className="text-zinc-700 text-sm">{t('taxes.yourTaxes')}</p>
          <NumberFlow
            format={{
              style: currencyFormatOptions.style,
              currency: currencyFormatOptions.currency,
              useGrouping: currencyFormatOptions.useGrouping
            }}
            className="text-[1.75rem] font-semibold text-zinc-900"
            value={taxes.totalTax}
          />
          <p className="text-xs text-zinc-500">
            {taxes.taxPercentage.toFixed(1)}% {t('taxes.fromIncome')}
          </p>
        </MotionDiv>

        {savings > 0 && <SavingsCard savings={savings} />}

        <TaxCard title={t('dashboard.incomeAndExpenses')} icon={<PiMoney className="w-5 h-5" />} delay={0.1}>
          <div className="divide-y divide-zinc-100">
            <div>
              <TaxRow label={t('dashboard.income')} value={totalIncome} delay={0.15} />
              {incomeItems.length > 0 && (
                <div className="pl-4 border-l-2 border-zinc-100 mt-1 mb-5 space-y-1">
                  {incomeItems.map((item, index) => (
                    <TaxRow key={item.key} label={item.description} value={item.amount} delay={0.16 + index * 0.01} isSmall />
                  ))}
                </div>
              )}
            </div>
            <div>
              <TaxRow label={t('dashboard.expenses')} value={totalExpenses} delay={0.2} />
              {expenseItems.length > 0 && (
                <div className="pl-4 border-l-2 border-zinc-100 mt-1 mb-5 space-y-1">
                  {expenseItems.map((item, index) => (
                    <TaxRow key={item.key} label={item.label} value={item.amount} delay={0.21 + index * 0.01} isSmall />
                  ))}
                </div>
              )}
            </div>
          </div>
          <TaxRow
            label={t('taxes.gpmBase')}
            value={taxes.gpmBase}
            isHighlight
            delay={0.25}
            info={t('taxes.gpmBaseInfo')}
          />
        </TaxCard>

        <TaxCard title={t('taxes.sodraVsdPsd')} icon={<PiReceipt className="w-5 h-5" />} delay={0.3} link={VAT_LINKS.sodraTarifai}>
          <div className="divide-y divide-zinc-100">
            <TaxRow
              label={t('taxes.sodraBase')}
              value={taxes.sodraBase}
              delay={0.35}
              info={t('taxes.sodraBaseInfo')}
            />
            <TaxRow
              label={t('taxes.vsdRate')}
              value={taxes.vsdAmount}
              delay={0.4}
              info={taxes.vsdWaivedReason
                ? t(taxes.vsdWaivedReason === 'first_year' ? 'taxes.vsdWaivedFirstYearInfo' : 'taxes.vsdWaivedStatusInfo')
                : t('taxes.vsdInfo')}
            />
            {!taxes.psdFloorWaived && (
              <TaxRow
                label={t('taxes.psdMonthlyLabel')}
                value={taxes.psdPaid}
                delay={0.45}
                info={t('taxes.psdMonthlyInfo', { months: dashboard?.months_active ?? 12 })}
              />
            )}
            <TaxRow
              label={t('taxes.psdOnProfitLabel')}
              value={taxes.psdDifference}
              delay={0.5}
              info={t('taxes.psdOnProfitInfo')}
            />
          </div>
          <TaxRow
            label={t('taxes.sodraTax')}
            value={taxes.vsdAmount + taxes.psdPaid + taxes.psdDifference}
            isHighlight
            delay={0.55}
          />
        </TaxCard>

        <TaxCard title={t('taxes.vmiGpm')} icon={<PiPercent className="w-5 h-5" />} delay={0.6} link={VAT_LINKS.vmiGPMInfo}>
            <div className="divide-y divide-zinc-100">
              <TaxRow
                label={t('taxes.taxableProfit')}
                value={taxes.gpmTaxableProfit}
                delay={0.65}
                info={t('taxes.taxableProfitInfo')}
              />
              <TaxRow
                label={t('taxes.gpmRateEffective')}
                value={taxes.gpmEffectiveRate}
                isPercentage
                delay={0.7}
                info={getGpmEffectiveRateInfo(financialYear, t)}
              />
              <TaxRow label={t('taxes.taxCredit')} value={taxes.gpmCredit} delay={0.75} info={t('taxes.taxCreditInfo')} />
          </div>
          <TaxRow label={t('taxes.gpmTax')} value={taxes.gpmAmount} isHighlight delay={0.8} />
        </TaxCard>

        <TaxCard title={t('taxes.result')} icon={<PiTrendUp className="w-5 h-5" />} delay={0.85}>
          <div className="divide-y divide-zinc-100">
            <TaxRow
              label={t('dashboard.income')}
              value={totalIncome}
              delay={0.9}
            />
            <TaxRow
              label={t('dashboard.expenses')}
              value={totalExpenses}
              delay={0.95}
            />
            <TaxRow
              label={t('dashboard.taxes')}
              value={taxes.totalTax}
              delay={1.0}
            />
          </div>
          <TaxRow label={t('taxes.profit')} value={taxes.netEarnings} isHighlight delay={1.05} />
        </TaxCard>

        <MotionDiv delay={1.1} className="mt-6">
          {savings > 0 && <SavingsDisclaimer actual={taxes} fixed={taxesWithFixed} />}
          <MotionText as="p" className="text-xs text-zinc-400 text-center leading-relaxed mt-3">
            {t('taxes.disclaimer')}
          </MotionText>
          <a
            href={VAT_LINKS.sodraCalculator}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 mt-3 text-xs text-blue-600 hover:text-blue-700"
          >
            {t('taxes.officialCalculator')}
            <PiArrowSquareOut className="w-3 h-3" />
          </a>
        </MotionDiv>
      </div>
      <Navigation />
    </div>
  )
}

const SavingsDisclaimer = ({ actual, fixed }: { actual: ReturnType<typeof calculateTaxes>; fixed: ReturnType<typeof calculateTaxes> }) => {
  const { t } = useTranslation()
  const toPercent = (rate: number) => `${(rate * 100).toFixed(2)}%`

  return (
    <p className="text-xs text-zinc-400 text-center leading-relaxed">
      {t('taxes.disclaimerFormula', { actualRate: toPercent(actual.gpmEffectiveRate), fixedRate: toPercent(fixed.gpmEffectiveRate) })}
    </p>
  )
}

export default DashboardTaxesPage
